import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";

// Lünefire Logo Component
const LunefireLogo = ({ size = "lg" }) => {
  const sizes = {
    md: { flame: "w-10 h-10", text: "text-2xl" },
    lg: { flame: "w-14 h-14", text: "text-3xl" }
  };
  const s = sizes[size] || sizes.lg;
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <Flame className={`${s.flame} text-[#DC2626]`} strokeWidth={2} />
        <Flame className={`${s.flame} text-[#FF8C00] absolute top-0 left-0 opacity-60`} strokeWidth={2} style={{ transform: 'scale(0.7) translate(20%, 30%)' }} />
      </div>
      <div className="text-center">
        <span className={`font-heading font-bold ${s.text} lunefire-gradient-text`}>Lünefire</span>
        <span className="block text-xs text-slate-500 tracking-widest">SNACKSTATION</span>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    
    setLoading(true);
    const success = await login(password);
    setLoading(false);
    
    if (success) {
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mb-4">
                <LunefireLogo size="lg" />
              </div>
              <CardDescription className="text-base">
                Feuerwehr Lüneburg
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Passwort eingeben
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12"
                      data-testid="password-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-12 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase tracking-wide btn-hover"
                  disabled={loading || !password.trim()}
                  data-testid="login-btn"
                >
                  {loading ? "Anmelden..." : "Anmelden"}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                Snackautomaten-Verwaltung
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right side - Gradient (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative lunefire-gradient">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center">
            <Flame className="w-24 h-24 mx-auto mb-6 drop-shadow-lg" />
            <h2 className="font-heading text-4xl font-bold mb-4 drop-shadow-lg">
              Immer einsatzbereit
            </h2>
            <p className="text-lg text-white/90 max-w-md">
              Verwalten Sie den Snackautomaten der Feuerwehr Lüneburg effizient und unkompliziert.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
